// HoldingsMapLayer — renders all published NASR HPF holding patterns inside
// the current viewport as MapLibre racetracks (LineString geometry).
//
// Data source: GET /api/bbox?layer=holdings&w&s&e&n&z=… (zoom-gated at z>=8).
// Re-fetches on pan/zoom/move-end. The racetrack body is computed client-side
// via holdingPatternRing() from the hold's fix lat/lon + inboundCourseDeg +
// turnDirection + leg-length text.
//
// Visual design:
//   - Stroke only (no fill) so multiple overlapping racetracks remain legible
//   - Standard right-turn holds: cyan (#22d3ee)
//   - Non-standard left-turn holds: amber (#f59e0b)
//   - Label at the fix: "FIXNAME 175°" (course shown rounded)
//
// Toggle: useUiStore `isLayerVisible('holdings')` via the LAYER_CATALOG entry.

import { useEffect, useRef } from 'react';
import { fetchBbox, type BboxHolding } from '../lib/mapBbox';
import { useLayerVisibilityStore } from '../stores/layerVisibilityStore';
import { getMapInstance } from './MapView';
import { holdingPatternRing } from '../lib/holdingPattern';
import maplibregl from 'maplibre-gl';

const SOURCE_ID = 'holdings-source';
const LINE_LAYER_ID = 'holdings-line';
const LABEL_LAYER_ID = 'holdings-label';

/**
 * Convert HPF text-form leg length into a structured object suitable for
 * holdingPatternRing. Examples:
 *   "1.0/"  → { kind: 'time', minutes: 1.0 }
 *   "/10"   → { kind: 'distance', nm: 10 }
 *   "1.5/8" → { kind: 'time', minutes: 1.5 }   (FAA prefers time when both)
 *   ""      → null  (caller defaults to 1 minute)
 */
export function parseHpfLegLength(s: string):
  | { kind: 'time'; minutes: number }
  | { kind: 'distance'; nm: number }
  | null {
  if (!s) return null;
  const trimmed = s.trim();
  if (!trimmed || trimmed === '/') return null;
  const m = /^(\d+(?:\.\d+)?)?\s*\/\s*(\d+(?:\.\d+)?)?$/.exec(trimmed);
  if (!m) return null;
  const minutesStr = m[1];
  const dmeStr = m[2];
  if (minutesStr && minutesStr.length > 0) {
    const minutes = parseFloat(minutesStr);
    if (Number.isFinite(minutes) && minutes > 0) {
      return { kind: 'time', minutes };
    }
  }
  if (dmeStr && dmeStr.length > 0) {
    const nm = parseFloat(dmeStr);
    if (Number.isFinite(nm) && nm > 0) {
      return { kind: 'distance', nm };
    }
  }
  return null;
}

interface BoundsRef {
  west: number; south: number; east: number; north: number; zoom: number;
}

/** Quantize a bbox so we don't re-fetch on micro-pans. */
function quantize(b: BoundsRef): string {
  const Q = 0.5; // 0.5° bucket
  return [
    Math.floor(b.west / Q) * Q,
    Math.floor(b.south / Q) * Q,
    Math.ceil(b.east / Q) * Q,
    Math.ceil(b.north / Q) * Q,
    Math.round(b.zoom),
  ].join('|');
}

async function refreshHoldings(signal: AbortSignal): Promise<void> {
  const map = getMapInstance();
  if (!map) return;
  const bnds = map.getBounds();
  const zoom = map.getZoom();
  if (zoom < 8) {
    // Below z=8 the racetracks are too small to read; clear the source.
    if (map.getSource(SOURCE_ID)) {
      (map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource).setData({
        type: 'FeatureCollection', features: [],
      });
    }
    return;
  }
  const items = await fetchBbox<'holdings'>({
    layer: 'holdings',
    west: bnds.getWest(), south: bnds.getSouth(),
    east: bnds.getEast(), north: bnds.getNorth(),
    zoom, limit: 200, signal,
  });
  if (signal.aborted) return;
  applyHoldings(items);
}

function applyHoldings(items: BboxHolding[]): void {
  const map = getMapInstance();
  if (!map) return;
  const features = items
    .filter((h) =>
      Number.isFinite(h.fixLat) &&
      Number.isFinite(h.fixLon) &&
      h.inboundCourseDeg != null,
    )
    .map((h) => {
      const turn: 'L' | 'R' = h.turnDirection === 'L' ? 'L' : 'R';
      const ring = holdingPatternRing({
        fixLat: h.fixLat,
        fixLon: h.fixLon,
        // HPF inboundCourseDeg is magnetic. At map-overlay zoom we render
        // as true \u2014 the magvar offset is < 1\u00b0 visually for a 6\u201310 NM
        // racetrack. (Caller can refine via fix-local magvar later.)
        inboundCourseTrueDeg: h.inboundCourseDeg as number,
        turnDirection: turn,
        legLength: parseHpfLegLength(h.legLength),
      });
      return {
        type: 'Feature' as const,
        geometry: { type: 'LineString' as const, coordinates: ring },
        properties: {
          fix: h.fixIdent,
          course: Math.round(h.inboundCourseDeg as number),
          turn,
          patternNumber: h.patternNumber,
          azimuth: h.azimuthType,
        },
      };
    });
  const fc: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features,
  };
  if (map.getSource(SOURCE_ID)) {
    (map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource).setData(fc);
  } else {
    map.addSource(SOURCE_ID, { type: 'geojson', data: fc });
    map.addLayer({
      id: LINE_LAYER_ID,
      type: 'line',
      source: SOURCE_ID,
      paint: {
        'line-color': [
          'case', ['==', ['get', 'turn'], 'L'],
          '#f59e0b',  // amber-500 for non-standard left turns
          '#22d3ee',  // cyan-400 for standard right turns
        ],
        'line-width': 1.5,
        'line-opacity': 0.7,
      },
    });
    map.addLayer({
      id: LABEL_LAYER_ID,
      type: 'symbol',
      source: SOURCE_ID,
      minzoom: 9,
      layout: {
        'symbol-placement': 'line-center',
        'text-field': ['concat', ['get', 'fix'], '  ', ['get', 'course'], '\u00b0'],
        'text-size': 10,
        'text-offset': [0, 0.6],
      },
      paint: {
        'text-color': [
          'case', ['==', ['get', 'turn'], 'L'],
          '#f59e0b', '#22d3ee',
        ],
        'text-halo-color': '#000',
        'text-halo-width': 1.5,
      },
    });
  }
}

function removeHoldingsFromMap(): void {
  const map = getMapInstance();
  if (!map) return;
  if (map.getLayer(LABEL_LAYER_ID)) map.removeLayer(LABEL_LAYER_ID);
  if (map.getLayer(LINE_LAYER_ID)) map.removeLayer(LINE_LAYER_ID);
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
}

/**
 * Toggle published holding patterns from FAA NASR HPF.txt on the en-route
 * map. Visibility is driven by the `holdings` layer in LAYER_CATALOG.
 */
export function HoldingsMapLayer(): null {
  const visible = useLayerVisibilityStore((s) => s.layers.holdings);
  const lastBboxRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!visible) {
      abortRef.current?.abort();
      abortRef.current = null;
      removeHoldingsFromMap();
      lastBboxRef.current = null;
      return;
    }
    const map = getMapInstance();
    if (!map) return;

    const onMoveEnd = () => {
      const bnds = map.getBounds();
      const key = quantize({
        west: bnds.getWest(), south: bnds.getSouth(),
        east: bnds.getEast(), north: bnds.getNorth(),
        zoom: map.getZoom(),
      });
      if (key === lastBboxRef.current) return;
      lastBboxRef.current = key;
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      refreshHoldings(ac.signal).catch((err: unknown) => {
        if ((err as { name?: string })?.name !== 'AbortError') {
          console.warn('[HoldingsMapLayer] refresh failed', err);
        }
      });
    };

    onMoveEnd();
    map.on('moveend', onMoveEnd);
    map.on('zoomend', onMoveEnd);
    return () => {
      abortRef.current?.abort();
      map.off('moveend', onMoveEnd);
      map.off('zoomend', onMoveEnd);
      removeHoldingsFromMap();
    };
  }, [visible]);

  return null;
}
